import AccessRestrictedPage from "./AccessRestrictedPage";
import useOrgAuthBranding from "../hooks/useOrgAuthBranding";
import { profileConnectWording } from "../utils/profileConnectWording";
import OrgAuthBrand from './brand/OrgAuthBrand';
import {organizationAuthContext, organizationActionSettings} from '../utils/organizationAuthContext';
import {OrganizationPermissionScopeProvider} from "../context/OrganizationPermissionScope";
import React, { useEffect, useState } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { requestVerificationEmail, verificationErrorMessage } from "../services/verificationDelivery";
import { auth } from "../firebaseConfig";
import { httpsCallable } from "firebase/functions";
import { auth as firebaseAuth, functions as firebaseFunctions } from "../firebaseConfig";
import { invalidatePermissionsCache, usePermissions } from "../hooks/usePermissions";
import { useResolvedOrgContext } from "../context/ResolvedOrgContext";
import { buildLoginUrl } from "../utils/authRedirect";
import { getOrgHomePath } from "../utils/canonicalPublicRoutes";
import { getCanonicalProtectedUrl } from "../utils/canonicalProtectedUrl";
import { getCurrentHostname } from "../utils/routing/hostUtils";
import { getRoleDefinition } from "../config/roles/churchRoleCatalog";
import { ADMIN_MODULE_BY_ID, canAccessAdminModule } from "../config/adminModuleRegistry";

/**
 * Route-level organization authorization (defense-in-depth on top of Firestore
 * rules). Unlike RequireAuth — which only confirms a user is signed in — this
 * guard confirms the signed-in user has *active access to the org in the route*
 * before any protected shell, skeleton, or Firestore read can mount.
 *
 * `require` selects the access level using the EXISTING permission model from
 * usePermissions (no new role model is introduced):
 *   - "member" : active owner/admin/member of the org (default)
 *   - "verifiedAccount" : an authenticated, email-verified account; this grants
 *     only the caller-owned profile and no organization authorization.
 *   - "admin"  : org owner or admin
 *   - "owner"  : org owner only
 *   - a permission key (e.g. "canManageAttendance") or array of keys: owner /
 *     admin, or any active member holding one of those capability booleans.
 */
export function hasRequiredOrgAccess(access = {}, require = "member") {
  if (require && typeof require === "object" && require.moduleId) {
    return canAccessAdminModule(require.moduleId, access);
  }
  const memberLevel = Boolean(access.isOwner || access.isAdmin || access.isMember || access.isPlatformAdmin);
  if (require === "verifiedAccount") {
    return Boolean(access.user?.uid && (access.user.emailVerified === true || access.user.phoneNumber));
  }
  if (require === "member") return memberLevel;
  if (require === "owner") return Boolean(access.isOwner);
  if (require === "admin") return Boolean(access.isOwner || access.isAdmin || access.isPlatformAdmin);

  // Capability gate: owner/admin always pass; otherwise the active member must
  // hold at least one of the requested permission booleans.
  if (access.isOwner || access.isAdmin || access.isPlatformAdmin) return true;
  const required = Array.isArray(require) ? require : [require];
  return required.some((permission) => Boolean(access.can?.(permission)));
}

export function AuthorizationLoading() {
  return (
    <div className="min-h-screen bg-[#f7f3ea] px-6 py-12 text-slate-700" aria-busy="true">
      <div className="mx-auto w-full max-w-6xl">
        <span className="sr-only" role="status">Verifying secure access</span>
        <div className="h-20 rounded-3xl border border-white/80 bg-white/85 shadow-sm">
          <div className="lightly-skeleton m-5 h-9 w-56 rounded-xl" />
        </div>
        <div className="mt-6 grid gap-5 lg:grid-cols-[280px_1fr]">
          <div className="lightly-skeleton hidden min-h-[620px] rounded-3xl lg:block" />
          <div className="rounded-3xl border border-white/80 bg-white/85 p-6 shadow-sm">
            <div className="lightly-skeleton h-8 w-64 rounded-xl" />
            <div className="lightly-skeleton mt-4 h-4 w-80 max-w-full rounded-lg" />
            <div className="mt-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {[0, 1, 2, 3, 4, 5].map((item) => (
                <div key={item} className="lightly-skeleton min-h-36 rounded-2xl" />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function AuthorizationError({ onRetry }) {
  return (
    <div className="min-h-screen bg-[#f7f3ea] px-6 py-20 text-center text-slate-700" role="alert">
      <div className="mx-auto max-w-md rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
        <h1 className="text-xl font-black text-slate-950">We couldn't verify your access</h1>
        <p className="mt-3 text-sm font-medium text-slate-600">
          Check your connection and try again. Your requested page has been preserved.
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-6 inline-flex min-h-11 items-center rounded-2xl bg-slate-950 px-5 text-sm font-black text-white hover:bg-slate-800"
        >
          Retry
        </button>
      </div>
    </div>
  );
}

/* Seconds between verification resends. Firebase rate-limits server side; this
 * keeps a member from burning that budget and being told to wait far longer. */
const RESEND_COOLDOWN_SECONDS = 60;

/**
 * Shown to a signed-in user whose email is not yet verified.
 *
 * Registration creates an account, not membership, and an unverified address is
 * not evidence of anything -- identity linking requires a Firebase-verified
 * email. So the honest thing to show here is "check your email", not "no
 * access", which reads as a refusal and sends people to an administrator who
 * cannot help.
 */
export function EmailVerificationPending({ user, onVerified, orgSlug }) {
  const location = useLocation();
  const resolved = useResolvedOrgContext();
  const branding = useOrgAuthBranding(orgSlug);
  const [cooldown, setCooldown] = useState(0);
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = window.setTimeout(() => setCooldown((n) => n - 1), 1000);
    return () => window.clearTimeout(id);
  }, [cooldown]);

  const resend = async () => {
    if (cooldown > 0 || busy) return;
    setBusy(true);
    setNotice("");
    try {
      const context = organizationAuthContext({search: `?org=${encodeURIComponent(orgSlug || '')}&returnTo=${encodeURIComponent(location.pathname + location.search + location.hash)}`}, {}, window.location.host, resolved?.isCustomDomain ? orgSlug : '');
      await requestVerificationEmail(user, organizationActionSettings(context));
      setCooldown(RESEND_COOLDOWN_SECONDS);
      setNotice("Verification email sent. It can take a few minutes to arrive.");
    } catch (error) {
      /* too-many-requests is the common one, and telling someone to "try again"
       * with no interval is useless. */
      setCooldown(RESEND_COOLDOWN_SECONDS);
      setNotice(verificationErrorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  /* Firebase caches emailVerified on the local user object, so a member who
   * verified in another tab stays blocked until the record is refreshed. Reload
   * the user AND force a fresh token, or a stale claim keeps them out. */
  const recheck = async () => {
    if (busy) return;
    setBusy(true);
    setNotice("");
    try {
      await user.reload();
      await user.getIdToken(true);
      if (auth.currentUser?.emailVerified) {
        /* The parent completes it: fresh token, parent auth state, linking,
         * cache invalidation and re-resolution. Remounting this screen would
         * only redisplay the same message. */
        await onVerified?.();
        return;
      }
      setNotice(
        "That address still isn't verified. Open the most recent email and use its link — older links expire once a newer one is sent.",
      );
    } catch {
      setNotice("We couldn't check just now. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f7f3ea] px-6 py-20 text-center text-slate-700">
      <div className="mx-auto max-w-md rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
        {orgSlug && <OrgAuthBrand {...branding} />}
        <h1 className="text-xl font-black text-slate-950">Verify your email address</h1>
        <p className="mt-3 text-sm font-medium text-slate-600">
          Check your email to verify your email address. After verification, return here to continue.
        </p>
        <p className="mt-3 text-sm font-black text-slate-900" data-testid="verification-email">
          {user?.email || "your email address"}
        </p>
        <p className="mt-3 text-sm font-medium text-slate-600">
          Emails can take a few minutes. Check spam, and always use the newest link —
          an older one stops working once you request another.
        </p>
        {notice ? (
          <p className="mt-4 text-sm font-bold text-slate-800" role="status">{notice}</p>
        ) : null}
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={recheck}
            disabled={busy}
            className="inline-flex min-h-11 items-center rounded-2xl bg-slate-950 px-4 text-sm font-black text-white shadow-sm hover:bg-slate-800 disabled:opacity-60"
          >
            I've verified my email
          </button>
          <button
            type="button"
            onClick={resend}
            disabled={busy || cooldown > 0}
            className="inline-flex min-h-11 items-center rounded-2xl border border-slate-300 bg-white px-4 text-sm font-black text-slate-800 disabled:opacity-60"
          >
            {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend verification email"}
          </button>
          <button
            type="button"
            onClick={() => signOut(auth)}
            className="inline-flex min-h-11 items-center rounded-2xl border border-slate-300 bg-white px-4 text-sm font-black text-slate-800"
          >
            Use a different email
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Why access was refused, in the caller's terms.
 *
 * One message for every cause is how a linked person came to be told to ask an
 * administrator for access they already had, and how a member missing one
 * capability is told they are not a member at all. Each cause has a different
 * remedy, so each gets its own sentence.
 */
export function denialCopyFor({ outcome = "", isMember = false, isLinkedPerson = false, organizationKind = {}, organizationName = "" } = {}) {
  if (isLinkedPerson || outcome === "linked_non_member") {
    return {
      title: "Member access required",
      body:
        "Your account is connected to your profile, but you don't currently have member access to this organization. " +
        "You can still view your own profile and giving.",
    };
  }
  if (isMember || outcome === "active_member") {
    /* An active member who failed a CAPABILITY check. Telling them they have no
     * membership would be false and would send them to the wrong person. */
    return {
      title: "Access restricted",
      body: "Your current account doesn’t have access to this area. Contact an owner or administrator if you need access.",
    };
  }
  if (outcome === "ambiguous_match" || outcome === "conflicting_link") {
    return {
      title: "Your profile connection needs review",
      body:
        "Your profile connection needs administrator review before this account can be connected. " +
        "Contact the organization and they can resolve it.",
    };
  }
  if (outcome === "archived_or_merged_person") {
    return {
      title: "Your profile connection needs review",
      body: "This account's profile connection needs administrator review. Contact the organization.",
    };
  }
  if (outcome === "no_matching_person") {
    return {
      title: "No matching profile",
      body: profileConnectWording(organizationKind, organizationName).accessNoMatch,
    };
  }
  if (outcome === "temporarily_unavailable") {
    return {
      title: "We couldn't check your access",
      body: "This is a temporary problem, not a refusal. Try again in a moment.",
    };
  }
  return {
    title: "No member access to this organization",
    body:
      "You don't have member access to this organization. If you believe this is a mistake, " +
      "ask an organization owner or admin to grant you access.",
  };
}

function OrgAccessDenied({ orgSlug, outcome = "", isMember = false, isLinkedPerson = false, user, role, requirement, onRetry, membershipStatus }) {
  const branding = useOrgAuthBranding(orgSlug);
  if (['pending', 'pending_approval', 'awaiting_approval'].includes(String(membershipStatus || '').toLowerCase())) {
    return <main className="min-h-screen bg-slate-50 px-4 py-12"><section className="mx-auto max-w-md rounded-2xl border border-slate-200 bg-white p-6">
      <OrgAuthBrand {...branding} />
      <h1 className="mt-5 text-2xl font-bold">Awaiting membership approval</h1>
      <p className="mt-3">Your account is signed in. {branding.name || 'Your organization'} needs to approve your membership before member features become available.</p>
      <a className="mt-5 inline-block" href={getOrgHomePath(orgSlug)}>Back to {branding.name || 'organization'} website</a>
    </section></main>;
  }
  const denialCopy = denialCopyFor({ outcome, isMember, isLinkedPerson, organizationKind: branding.organizationKind, organizationName: branding.name });
  const module = requirement?.moduleId ? ADMIN_MODULE_BY_ID[requirement.moduleId] : null;
  const readable = value => String(value || "").replace(/^can(?=[A-Z])/, "").replace(/([a-z])([A-Z])/g, "$1 $2").replaceAll("_", " ").replace(/\b\w/g, c => c.toUpperCase());
  const areaLabel = module?.label || (Array.isArray(requirement) ? requirement.map(readable).join(" or ") : ({admin: "Administration", owner: "Owner access", member: "Member area"}[requirement] || readable(requirement)));
  const roleLabel = getRoleDefinition(role)?.defaultLabel || (role ? readable(role) : isMember ? "Member" : "No active membership");
  return <AccessRestrictedPage
    organizationName={branding.name}
    homePath={orgSlug ? getOrgHomePath(orgSlug) : "/"}
    title={denialCopy.title}
    description={denialCopy.body}
    areaLabel={areaLabel}
    accountLabel={user?.email || user?.phoneNumber || "Email unavailable"}
    roleLabel={roleLabel}
    onSwitchAccount={() => signOut(auth)}
    onRetry={onRetry}
  />;
}

// Inner gate: remounted per-org via `key` so usePermissions always resolves
// against the current slug and a stale org's access can never leak through.
function OrgAccessGate({
  orgSlug, require, children, emailVerified = true, user = null, onVerified,
}) {
  const access = usePermissions(orgSlug);

  // While the org id resolves / membership loads, never render protected
  // content (avoids protected shells, skeletons, and Firestore reads).
  if (access.authorizationStatus === "error") {
    return <AuthorizationError onRetry={access.retry} />;
  }

  const resolvingOrgAccess = access.authorizationStatus === "loading" || access.loading || !access.orgId;
  if (resolvingOrgAccess) return <AuthorizationLoading />;

  const meetsRequirement = hasRequiredOrgAccess(access, require);

  /* Verification is offered ONLY where a link is what would grant access --
   * that is, /me and /my-giving. On an admin or capability route it would imply
   * that confirming an email leads to administrative access, which is false and
   * would send people around a loop that cannot end in what they wanted. Those
   * routes keep their proper denial. */
  const verificationCanHelp = require === "verifiedAccount";
  if (
    verificationCanHelp &&
    !meetsRequirement &&
    user &&
    !emailVerified &&
    !access.isMember
  ) {
    return (
      <EmailVerificationPending user={user} onVerified={onVerified} orgSlug={orgSlug} />
    );
  }

  /* authorizationStatus "denied" means "not an active member" -- which is
   * exactly the state a linked non-member is in. Deciding on the requirement
   * alone keeps every existing level identical (they all already require an
   * active member) while letting the linked level through. */
  if (!meetsRequirement) {
    return (
      <OrgAccessDenied
        orgSlug={orgSlug}
        outcome={access.accessOutcome || ""}
        isMember={Boolean(access.isMember)}
        isLinkedPerson={Boolean(access.isLinkedPerson)}
        user={user}
        role={access.role}
        requirement={require}
        onRetry={access.retry}
        membershipStatus={access.membership?.status || access.status}
      />
    );
  }

  return <OrganizationPermissionScopeProvider requestKey={orgSlug} orgId={access.orgId}>{children}</OrganizationPermissionScopeProvider>;
}

export default function RequireOrgAccess({ children, require = "member", orgSlug: orgSlugProp }) {
  const location = useLocation();
  const params = useParams();
  // Custom-domain routes carry no :orgSlug param; the org comes from the
  // resolved-org context provided by the custom-domain shell.
  const resolvedOrgContext = useResolvedOrgContext();
  const orgSlug = String(
    orgSlugProp || params.orgSlug || resolvedOrgContext?.orgSlug || "",
  ).trim();

  const canonicalUrl = getCanonicalProtectedUrl({
    orgSlug, hostname: getCurrentHostname(),
    pathname: location.pathname, search: location.search, hash: location.hash,
  });
  useEffect(() => {
    if (canonicalUrl) window.location.replace(canonicalUrl);
  }, [canonicalUrl]);

  // Auth subscription lives in the stable outer component so switching between
  // orgs does not re-flash the auth loading state.
  const [authLoading, setAuthLoading] = useState(!auth.currentUser);
  const [user, setUser] = useState(auth.currentUser);
  /* Bumped once the post-verification transition finishes, to remount the gate
   * so access is resolved again from scratch. */
  const [accessNonce, setAccessNonce] = useState(0);
  const [transitioning, setTransitioning] = useState(false);

  /**
   * Finish verification properly.
   *
   * Reloading the user is not enough on its own: the ID token still carries the
   * old email_verified claim, the parent still holds the stale user object, no
   * link has been attempted yet, and usePermissions would answer from its cache.
   * Each step below exists because skipping it leaves the member looking at the
   * same screen after doing exactly what it asked.
   */
  const completeVerification = async () => {
    setTransitioning(true);
    try {
      const current = firebaseAuth.currentUser;
      if (current) {
        await current.reload();
        await current.getIdToken(true);
        setUser(firebaseAuth.currentUser);
      }
      /* Linking is a WRITE and belongs here -- at the moment verification makes
       * it legitimate -- not in the route guard's read-only access check.
       * Swallowed: a linking failure must not strand the member on this screen,
       * and the re-resolution below will simply find no link. */
      try {
        const ensureLink = httpsCallable(firebaseFunctions, "ensureIdentityLinkForCurrentUser");
        await ensureLink(orgSlug ? { orgId: orgSlug } : {});
      } catch {
        // Non-fatal by design.
      }
      invalidatePermissionsCache();
      setAccessNonce((n) => n + 1);
    } finally {
      setTransitioning(false);
    }
  };
  useEffect(
    () =>
      onAuthStateChanged(auth, (currentUser) => {
        setUser(currentUser);
        setAuthLoading(false);
      }),
    [],
  );

  /* Hold the loading state across the whole transition so the member never
   * sees the verification screen flash back before resolution completes. */
  if (canonicalUrl || authLoading || transitioning) return <AuthorizationLoading />;
  if (!user) {
    return <Navigate to={buildLoginUrl(location, {orgSlug})} replace state={{ from: location }} />;
  }

  // Defensive: a route with no org slug cannot be org-scoped; keep it behind
  // authentication only rather than failing closed.
  if (!orgSlug) return children;

  return (
    <OrgAccessGate
      key={`${orgSlug}:${accessNonce}`}
      orgSlug={orgSlug}
      require={require}
      user={user}
      emailVerified={user.emailVerified !== false}
      onVerified={completeVerification}
    >
      {children}
    </OrgAccessGate>
  );
}
